import Config from 'common-src/config';
import { generateAthenaUrl } from 'common-src/models/PatientIntegrationsAthena';
import { TaskReason, TaskType } from 'common-src/models/PatientTask/constants';

import {
  ASSESSMENT_TASK_TYPES,
  ASSESSMENT_TASK_VARIANTS,
  BLOCKED_STATUS,
  CARE_ORCHESTRATION_BASE_PATH,
  ESCALATED_STATUS,
  TAB_KEYS,
  TASK_CALLBACK_STATUS,
  TASK_DISPLAY_STATUS,
  TASK_DISPLAY_STATUS_BY_STATUS,
  TASK_PRIORITY,
  TASK_STATUS_FILTER_KEYS,
} from './constants';

export const getPatient = (id, patients = []) => patients.find((p) => p.id === id);
export const getTask = (id, tasks = []) => tasks.find((t) => t.id === id);
export const athenaUrl = (athenaPatientId, practiceIdOverride) => {
  const practiceId = practiceIdOverride ?? JSON.parse(Config.ATHENA_MINDSPAN_GROUP_PRACTICE)?.value;
  if (!practiceId || !athenaPatientId) return null;
  return generateAthenaUrl({ practiceId, athenaPatientId });
};

export const daysUntil = (d) => Math.round((new Date(d) - new Date()) / 86400000);

export const formatDue = (d) => {
  const n = daysUntil(d);
  if (n < 0) return { text: `${Math.abs(n)}d overdue`, urgent: true };
  if (n === 0) return { text: 'Today', urgent: true };
  if (n === 1) return { text: 'Tomorrow', urgent: false };
  return { text: `in ${n}d`, urgent: false };
};

export const overallReadiness = (r) => {
  const values = Object.values(r);
  if (values.some((item) => item.status === 'red')) return 'red';
  if (values.some((item) => item.status === 'yellow')) return 'yellow';
  return 'green';
};

export const getTaskDisplayStatus = (task) =>
  TASK_DISPLAY_STATUS_BY_STATUS[task?.status] ?? TASK_DISPLAY_STATUS.Pending;

export const isTaskReadinessComplete = (task) => {
  if (getTaskDisplayStatus(task) === TASK_DISPLAY_STATUS.Done) return true;
  const callbackStatus = `${task?.callbackStatus || ''}`.trim().toLowerCase();
  return callbackStatus === TASK_CALLBACK_STATUS.Success;
};

export const isTaskVisible = (task) => task?.isVisible ?? true;

export const isLinkedTask = (task) => task?.isVisible === false;

export const canMarkTaskComplete = (task) => task?.isVisible ?? true;

export const shouldHideTaskByCallbackStatus = (task) => {
  if (getTaskDisplayStatus(task) === TASK_DISPLAY_STATUS.Done) return true;

  const normalized = `${task?.callbackStatus || ''}`.trim().toLowerCase();
  return normalized === TASK_CALLBACK_STATUS.Success;
};

export const isTaskBlockedStatus = (task) => task?.blockedStatus === BLOCKED_STATUS.Blocked;

export const isTaskEscalatedStatus = (task) =>
  task?.escalatedStatus != null && task.escalatedStatus === ESCALATED_STATUS.Escalated;

export const isMessageTask = (task) =>
  task?.typeId === TaskType.MemberPortal.value &&
  task?.reasonId === TaskReason.NewMessageReceived.value;

const hasCareOrchestrationMetaEntry = (task) => !!`${task?.team || ''}`.trim();

export const isAssessmentTask = (task) =>
  task?.typeId === TaskType.Cognitive.value && hasCareOrchestrationMetaEntry(task);

export const getAssessmentTaskTypeByValue = (value) =>
  Object.values(ASSESSMENT_TASK_TYPES).find((assessmentType) => assessmentType.value === value);

export const getTaskAssessmentType = (task) => `${task?.assessmentType ?? ''}`.trim();

export const getTaskAssessmentTypeLabel = (task) => {
  const assessmentType = getTaskAssessmentType(task);
  if (!assessmentType) return '';

  return getAssessmentTaskTypeByValue(assessmentType)?.label ?? assessmentType;
};

export const getAssessmentTaskVariantByValue = (value) =>
  Object.values(ASSESSMENT_TASK_VARIANTS).find(
    (assessmentVariant) => assessmentVariant.value === value,
  );

export const getTaskAssessmentVariant = (task) => `${task?.assessmentVariant ?? ''}`.trim();

export const getTaskAssessmentVariantLabel = (task) => {
  const assessmentVariant = getTaskAssessmentVariant(task);
  if (!assessmentVariant) return '';

  return getAssessmentTaskVariantByValue(assessmentVariant)?.label ?? assessmentVariant;
};

export const isTaskCaregiverAssisted = (task) => !!task?.caregiverAssisted;

export const getTaskDisplayTitle = (task, fallback = '') => {
  const assessmentTypeLabel = getTaskAssessmentTypeLabel(task);
  if (assessmentTypeLabel) return `Please review the ${assessmentTypeLabel} screening`;

  return task?.title ?? task?.subject ?? fallback;
};

export const getCareOrchestrationPath = (view, taskRoute = null) => {
  if (!view) return CARE_ORCHESTRATION_BASE_PATH;

  if (view === TAB_KEYS.TASKS) {
    const segments = [CARE_ORCHESTRATION_BASE_PATH, view];

    if (taskRoute?.statusFilter) {
      segments.push(taskRoute.statusFilter);
    }

    if (taskRoute?.patientId && taskRoute?.taskId) {
      segments.push(taskRoute.patientId, taskRoute.taskId);
    }

    return taskRoute?.statusFilter || (taskRoute?.patientId && taskRoute?.taskId)
      ? segments.join('/')
      : `${CARE_ORCHESTRATION_BASE_PATH}/${view}`;
  }

  return `${CARE_ORCHESTRATION_BASE_PATH}/${view}`;
};

export const parseCareOrchestrationRoute = (pathname) => {
  if (!pathname.startsWith(CARE_ORCHESTRATION_BASE_PATH)) {
    return { view: null, statusFilter: null, patientId: null, taskId: null };
  }

  const pathSuffix = pathname.slice(CARE_ORCHESTRATION_BASE_PATH.length).replace(/^\/+/, '');
  const [view = null, firstSegment = null, secondSegment = null, thirdSegment = null] =
    pathSuffix.split('/');

  if (view === TAB_KEYS.TASKS) {
    const statusFilter = TASK_STATUS_FILTER_KEYS.includes(firstSegment) ? firstSegment : null;

    return {
      view,
      statusFilter,
      patientId: statusFilter ? secondSegment : firstSegment,
      taskId: statusFilter ? thirdSegment : secondSegment,
    };
  }

  return {
    view,
    statusFilter: null,
    patientId: null,
    taskId: null,
  };
};

export const getTaskPatientId = (task) =>
  task?.patientId ??
  task?.patient_id ??
  task?.patient?.id ??
  task?.patient?.patientId ??
  task?.patient?.patient_id ??
  null;

export const getAppointmentPatientId = (appointment) =>
  appointment?.patientId ??
  appointment?.patient_id ??
  appointment?.patient?.id ??
  appointment?.patient?.patientId ??
  appointment?.patient?.patient_id ??
  null;

export const getTaskPatientGroupKey = (task) => {
  const patientId = getTaskPatientId(task);
  return patientId != null ? `patient-${patientId}` : `task-${task?.id}`;
};

export const formatPatientMrn = (mrn) => {
  if (!mrn) return null;
  return String(mrn).startsWith('MRN-') ? mrn : `MRN-${mrn}`;
};

export const getTaskDeepLinkUrl = (task, origin) => {
  const patientId = getTaskPatientId(task);
  const resolvedOrigin = origin ?? (typeof window !== 'undefined' ? window.location.origin : null);
  if (!task?.id || patientId == null || !resolvedOrigin) return null;

  return `${resolvedOrigin}${getCareOrchestrationPath(TAB_KEYS.TASKS, {
    patientId: `${patientId}`,
    taskId: `${task.id}`,
  })}`;
};

export const copyTextToClipboard = async (text) => {
  if (!text || typeof document === 'undefined') return false;

  if (navigator?.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      // Fall back to document.execCommand below for insecure dev origins.
    }
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.opacity = '0';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  textArea.setSelectionRange(0, text.length);

  let didCopy = false;

  try {
    didCopy = document.execCommand('copy');
  } finally {
    document.body.removeChild(textArea);
  }

  return didCopy;
};

export const matchesTaskStatusFilter = (task, statusFilter) => {
  if (!task) return false;

  switch (statusFilter) {
    case 'open':
      return getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done;
    case 'critical':
      return task.priority === TASK_PRIORITY.Critical;
    case 'blocked':
      return isTaskBlockedStatus(task);
    case 'escalated':
      return isTaskEscalatedStatus(task);
    case 'messages':
      return isMessageTask(task) && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done;
    case 'assessments':
      return isAssessmentTask(task) && getTaskDisplayStatus(task) !== TASK_DISPLAY_STATUS.Done;
    default:
      return true;
  }
};

export const getFirstVisibleIncompleteSiblingTask = (
  task,
  tasks = [],
  fallbackAppointmentId = null,
) => {
  if (!task) return null;

  const appointmentId = `${task?.externalObjectId || fallbackAppointmentId || ''}`.trim();
  if (!appointmentId) return null;

  return (
    tasks.find((candidateTask) => {
      if (!candidateTask || candidateTask?.id === task?.id) return false;

      const candidateAppointmentId =
        `${candidateTask?.externalObjectId || fallbackAppointmentId || ''}`.trim();
      if (candidateAppointmentId !== appointmentId) return false;

      return isTaskVisible(candidateTask) && !isTaskReadinessComplete(candidateTask);
    }) ?? null
  );
};

export const getAppointmentReadinessStatus = (tasks = []) => {
  if (
    tasks.some(
      (task) => !isTaskReadinessComplete(task) && task?.priority === TASK_PRIORITY.Critical,
    )
  ) {
    return 'red';
  }
  if (tasks.some((task) => !isTaskReadinessComplete(task))) return 'yellow';
  if (tasks.length > 0) return 'green';
  return 'default';
};

export const dayName = (d) =>
  ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(d).getDay()];

export const monthDay = (d) => {
  const dt = new Date(d);
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][dt.getMonth()]} ${dt.getDate()}`;
};

export const sortAppointmentsByDate = (appointments = []) =>
  [...appointments].sort((a, b) => new Date(a.date) - new Date(b.date));

export const groupAppointmentsByDate = (appointments) => {
  const grouped = {};
  appointments.forEach((appt) => {
    (grouped[appt.date] = grouped[appt.date] || []).push(appt);
  });
  return grouped;
};

const normalizeProviderAthenaId = (providerAthenaId) => {
  if (providerAthenaId == null || providerAthenaId === '') return null;
  return `${providerAthenaId}`.trim();
};

export const getClientByAthenaProviderId = (clients = [], providerAthenaId) => {
  const normalizedProviderAthenaId = normalizeProviderAthenaId(providerAthenaId);
  if (!normalizedProviderAthenaId) return null;

  return (
    clients.find(
      (client) =>
        normalizeProviderAthenaId(client?.athenaProviderId) === normalizedProviderAthenaId,
    ) ?? null
  );
};

export const formatClinicianName = (client) => {
  const firstName = `${client?.firstName || ''}`.trim();
  const lastName = `${client?.lastName || ''}`.trim();
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  const credentials = `${client?.credentials || ''}`.trim();

  if (!fullName) return '-';
  return credentials ? `${fullName}, ${credentials}` : fullName;
};
